import { Controller, type UseFormReturn } from 'react-hook-form';
import type { ContentProfile } from '@content-planner/shared';
import {
  audienceChoices,
  businessTypeChoices,
  goalChoices,
  nextTwelveMonths,
  platformChoices,
  toneChoices,
} from './options.js';

type FormShape = ContentProfile & { month?: string };

function labelFor<V extends string>(
  list: ReadonlyArray<{ value: V; label: string }>,
  value: V | undefined,
): string {
  return list.find((c) => c.value === value)?.label ?? '—';
}

export function StepRound3({ form }: { form: UseFormReturn<FormShape> }) {
  const values = form.watch();
  const months = nextTwelveMonths();

  return (
    <div>
      <h2 className="mb-1">Confirm and pick your month.</h2>
      <p className="text-text-secondary mb-6">
        Review your profile and tell us which month this plan is for.
      </p>

      <div className="gcm-card mb-6">
        <h3 className="mb-3">Your profile</h3>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <Row label="Business" value={
            values.businessType === 'other' && values.businessTypeOther
              ? values.businessTypeOther
              : labelFor(businessTypeChoices, values.businessType)
          } />
          <Row label="Audience" value={
            values.audience === 'other' && values.audienceOther
              ? values.audienceOther
              : labelFor(audienceChoices, values.audience)
          } />
          <Row label="Goal" value={labelFor(goalChoices, values.goal)} />
          <Row
            label="Platforms"
            value={
              (values.platforms ?? [])
                .map((p) => labelFor(platformChoices, p))
                .join(', ') || '—'
            }
          />
          <Row label="Tone" value={labelFor(toneChoices, values.tone)} />
          <Row label="Avoid" value={values.avoid?.trim() || '—'} />
          <Row
            label="Great 8 brand foundation"
            value={
              values.great8 && values.great8.trim().length > 0
                ? `Included — ${values.great8.trim().length.toLocaleString()} characters of brand context`
                : 'Not provided'
            }
          />
          <Row
            label="Business description"
            value={values.businessDescription?.trim() || 'Not provided'}
          />
        </dl>
      </div>

      <Controller
        name="month"
        control={form.control}
        rules={{ required: 'Pick a month.' }}
        render={({ field, fieldState }) => (
          <label className="block mb-6">
            <span className="font-heading font-bold text-base">Which month is this plan for?</span>
            <select
              className="gcm-input mt-2"
              value={field.value ?? ''}
              onChange={field.onChange}
            >
              <option value="" disabled>
                Choose a month
              </option>
              {months.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            {fieldState.error && (
              <p className="text-sm text-danger mt-1">{fieldState.error.message}</p>
            )}
          </label>
        )}
      />
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="gcm-eyebrow">{label}</dt>
      <dd className="text-text-primary">{value}</dd>
    </div>
  );
}
